import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";

const ITEM_TEMPLATE =
  "<div class='py-1'>" +
  "<div class='font-semibold text-sm'>${text}</div>" +
  "<div class='text-xs text-gray-500 dark:text-gray-400'>${description}</div>" +
  "</div>";

/**
 * LoanTypeSelect — Syncfusion DropDownList pre-wired to the loanTypes Redux store.
 *
 * Emits the selected `parameterId` via `onChange` and the loan-type
 * `code` (e.g. "REFI", "CONS") via `onCodeChange`.
 */
export default function LoanTypeSelect({
  placeholder = "Loan Type *",
  value,
  onChange,
  onCodeChange,
  error,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  const { loanTypes: records } = useSelector(state => state.loanTypes);

  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(lt => ({
      text: lt.name,
      value: lt.parameterId,
      code: lt.code,
      description: lt.description || "",
    }));
  }, [records]);

  const handleChange = useCallback(
    e => {
      const item = e.itemData;
      onChange?.(item?.value ?? undefined);
      onCodeChange?.(item?.code ?? null);
    },
    [onChange, onCodeChange],
  );

  return (
    <div className={classNames("select-input-wrapper", className)}>
      <DropDownListComponent
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        dataSource={dataSource}
        fields={{ text: "text", value: "value" }}
        value={value}
        change={handleChange}
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        itemTemplate={ITEM_TEMPLATE}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

LoanTypeSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  /** Called with the loan-type code string (e.g. "REFI", "CONS") on change */
  onCodeChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

